import { useQuery } from '@apollo/client';
import cookies from 'browser-cookies';
import React, { Component } from 'react';

import errorBoundaryHOC from 'components/Errors/ErrorBoundary/errorBoundaryHOC';
import Loading from 'components/Loading';
import { ModalPrivacyAgreement } from 'components/ModalPrivacyAgreement/ModalPrivacyAgreement'

import { IS_BETA } from 'constants/DEBUG';
import { SubscriptionType } from 'constants/SUBSCRIPTION';
import CHECK_TOKEN from 'context/AppPersonContext/_gql/CHECK_TOKEN';
import GET_APP_PROFILE_NOTIFICATIONS_COUNT from 'context/AppPersonContext/_gql/GET_APP_PROFILE_NOTIFICATIONS_COUNT';
import GET_SUBSCRIPTION from 'gql/queries/GET_SUBSCRIPTION';
import getQueryKey from 'helpers/getQueryKey';
import GQLHOC from 'hocs/GQLHOC';

import appPerson_initial_state from './appPerson_initial_state';
import AppPersonContext from './AppPersonContext';
import makePersonsMainMenu from './makePersonsMainMenu';

@errorBoundaryHOC({title: 'Error loading application user'})
@GQLHOC(function (props) {
  const { sport_id } = props
  const token = cookies.get('auth.token');
  const user_query = useQuery(CHECK_TOKEN, {
    skip: !token,
    variables: { token },
  })
  const auser_id = user_query?.data?.[getQueryKey(CHECK_TOKEN)]?.user?.auser_id
  const notifications_count_query = useQuery(GET_APP_PROFILE_NOTIFICATIONS_COUNT, {
    variables: {
      ...sport_id ? { sport_id } : {},
    },
    pollInterval: IS_BETA ? 600000000000000000 : 30000,
    skip: !auser_id
  })
  const sport_vokrug_plus_subscription_query = useQuery(GET_SUBSCRIPTION, {
    skip: !auser_id,
    variables: {
      subscription_type: SubscriptionType.SPORT_VOKRUG_PLUS,
    },
  })

  return {
    user_query,
    notifications_count_query,
    sport_vokrug_plus_subscription_query,
  }
})
@errorBoundaryHOC({title: 'Application error (AppPerson)'})
export default class AppPersonState extends Component {
  state = appPerson_initial_state

  render () {
    const {
      is_admin,
      is_federator,
      is_super_federator,
      is_moderator,
      is_guest,
      is_user,
      email,
      phone,
      last_application_phone,
      has_organizations,
      get_is_organization_responsible,
      has_calendars,
      has_active_organizations,
      user_id,
      self_is_admin,
      self_is_moderator,
      menu,
      metrika_allowed,
      user,
      self,
      notifications_count,
      toggleMobileMenu,
      default_sport_key,
      sport_vokrug_plus_subscription,
      sport_vokrug_plus_subscription_status,
      has_sport_vokrug_plus_subscription,
      sport_vokrug_plus_subscription_loading,
      sport_vokrug_plus_subscription_error,
      props: {
        children,
        user_query: {
          loading,
        } = {},
      }
    } = this

    if (loading) {
      return <Loading />
    }

    return (
      <AppPersonContext.Provider
        value={{
          ...this.state,
          user,
          email,
          phone,
          last_application_phone,
          self,
          is_admin,
          is_federator,
          is_super_federator,
          is_moderator,
          has_organizations,
          has_active_organizations,
          get_is_organization_responsible,
          has_calendars,
          metrika_allowed,
          user_id,
          is_guest,
          is_user,
          self_is_admin,
          self_is_moderator,
          notifications_count,
          toggleMobileMenu,
          menu,
          default_sport_key,
          sport_vokrug_plus_subscription,
          sport_vokrug_plus_subscription_status,
          has_sport_vokrug_plus_subscription,
          sport_vokrug_plus_subscription_loading,
          sport_vokrug_plus_subscription_error,
        }}
      >
        {Boolean(!self_is_admin || (self?.auser_id && user?.auser_id && self.auser_id === user.auser_id)) &&
          <ModalPrivacyAgreement
            hasAgreementWithActiveDocument={this.hasAgreementWithActiveDocument}
          />
        }
        {children}
      </AppPersonContext.Provider>
    )
  }

  componentDidMount () {
    const {
      user,
      self,
      self_is_admin,
      self_is_moderator
    } = this;
    // если пользователь не админ и не модератор, то ему нельзя просматривать сайт под чужим логином
    // эта проерка происходит на бекенде, здесь бекап если вдруг там что-то сломается
    if (self?.user_id && user?.user_id && self.user_id !== user.user_id && !self_is_admin && !self_is_moderator) {
      throw new Error('You do not have enough rights to watch this page under this user. Please, login as admin or moderator.');
    }
  }

  get email () {
    return this.user.email
  }

  get phone () {
    return this.user.phone
  }

  get last_application_phone () {
    return this.user.last_application_phone
  }

  get has_organizations () {
    return Boolean(this.user?.roles?.has_organizations)
  }

  get has_active_organizations () {
    return Boolean(this.user?.roles?.has_active_organizations)
  }

  /**
   *
   * @param {Object} options
   * @param {Int} options.organization_id - id организации, по которой нужно отфильтровать организации
   * @param {Boolean} options.include_subordinates - включать ли подчинённые организации
   * @param {Array} options.status_filter - массив статусов, по которым нужно отфильтровать организации
   * @param {Int} options.parents_organization_id - этот id есть среди родительских организаций
   * @param {Int} options.not_parents_organization_id - этот id не должен быть среди родительских организаций
   * @returns
   */
  get_is_organization_responsible = (options = {}) => {
    const {
      user: {
        roles: {
          organization_tree
        } = {}
      } = {}
    } = this;

    const {
      include_subordinates = true,
      status_filter = 'all',
      organization_id = 'all',
      parents_organization_id = 'all',
      not_parents_organization_id = 'all',
    } = options;

    if (!organization_tree?.length) return false;

    return organization_tree.some(item =>
        (organization_id === 'all' || Number(item.organization_id) === Number(organization_id))
        && (include_subordinates || item.level === 0)
        && (status_filter === 'all' || status_filter.includes(item.revise_state))
        && (parents_organization_id === 'all' || (item.branchmark || []).includes(Number(parents_organization_id)))
        && (not_parents_organization_id === 'all' || !item.branchmark.includes(Number(not_parents_organization_id)))
        // здесь ищем только подчиненных, у вышестоящих будет level < 0
        && item.level >= 0
    );
  }

  get has_calendars () {
    return Boolean(this.user?.roles?.has_calendars)
  }

  get is_admin () {
    return Boolean(this.user?.roles?.is_admin)
  }

  get is_federator () {
    return Boolean(this.user?.roles?.is_federator)
  }


  get is_super_federator () {
    return Boolean(this.user?.roles?.is_super_federator)
  }

  get is_guest () {
    return !this.user.auser_id
  }

  get is_moderator () {
    return Boolean(this.user?.roles?.is_moderator)
  }

  get is_user () {
    return Boolean(this.user.auser_id)
  }

  get menu () {
    return this.makeMenu()
  }

  get metrika_allowed () {
    // return !this.is_admin
    return true
  }

  get notifications_count () {
    return this.props?.notifications_count_query?.appUserAlerts || {}
  }

  get sport_vokrug_plus_subscription () {
    const query = this.props?.sport_vokrug_plus_subscription_query

    return query?.data?.subscription || query?.subscription || null
  }

  get sport_vokrug_plus_subscription_status () {
    return this.sport_vokrug_plus_subscription?.status || null
  }

  get has_sport_vokrug_plus_subscription () {
    const status = this.sport_vokrug_plus_subscription_status

    return (status?.sid === 'active' || status?.sid === 'canceled') && !status?.expired
  }

  get sport_vokrug_plus_subscription_loading () {
    return Boolean(this.props?.sport_vokrug_plus_subscription_query?.loading)
  }

  get sport_vokrug_plus_subscription_error () {
    return this.props?.sport_vokrug_plus_subscription_query?.error || null
  }

  get default_sport_key () {
    return this.user?.default_sport_key
  }

  // это реальный пользователь (не тот, под которым смотрят сайт)
  get self () {
    return this.user_info.self
  }

  // это реальный пользователь (не тот, под которым смотрят сайт)
  get self_is_admin () {
    return Boolean(this.self?.roles?.is_admin)
  }

  // это реальный пользователь (не тот, под которым смотрят сайт)
  get self_is_moderator () {
    return Boolean(this.self?.roles?.is_moderator)
  }

  get user () {
    return this.user_info.user
  }

  get user_id () {
    return this.user.auser_id
  }

  get user_info () {
    const {
      props: {
        user_query: {
          checkToken,
        },
      },
    } = this;

    if (checkToken) {
      return checkToken;
    }

    return {
      user: appPerson_initial_state.user,
      self: appPerson_initial_state.self,
    }
  }

  get hasAgreementWithActiveDocument () {
    return this.user?.hasAgreementWithActiveDocument
  }

  makeMenu = () => {
    const {
      state: {
        session: {
          app_person = {}
        } = {}
      }
    } = this

    return makePersonsMainMenu(app_person)
  }

  toggleMobileMenu = () => {
    this.setState(
      (state) => {
        return {
          ...state,
          ui: {
            ...state.ui,
            isMobileMenuOpen: !state.isMobileMenuOpen
          }
        }
      }
    )
  }
}
